import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import gsap from 'gsap';
import asterisk from '../asserts/as.png';
import './Ticketviewpage.css';

const QRCode = ({ ticketId }) => {
    const size = 150;
    const cells = 25;
    const cellSize = size / cells;
    const seed = ticketId ? ticketId.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 99;

    const pseudo = (r, c) => {
        const v = (seed * 31 + r * 19 + c * 11 + r * c * 7 + r * 3 + c * 5) % 100;
        return v < 48;
    };

    const inFinder = (r, c) =>
        (r < 8 && c < 8) || (r < 8 && c >= cells - 8) || (r >= cells - 8 && c < 8);

    const finderBlock = (x, y) => [
        <rect key={`f1-${x}-${y}`} x={x} y={y} width={7 * cellSize} height={7 * cellSize} fill="#111"/>,
        <rect key={`f2-${x}-${y}`} x={x + cellSize} y={y + cellSize} width={5 * cellSize} height={5 * cellSize} fill="white"/>,
        <rect key={`f3-${x}-${y}`} x={x + 2 * cellSize} y={y + 2 * cellSize} width={3 * cellSize} height={3 * cellSize} fill="#111"/>,
    ];

    return (
        <svg aria-label={`QR Code for ticket ID ${ticketId}`} role="img" width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', borderRadius: '4px' }}>
            <rect width={size} height={size} fill="white"/>
            {Array.from({ length: cells }, (_, r) =>
                Array.from({ length: cells }, (_, c) => {
                    if (inFinder(r, c)) return null;
                    return pseudo(r, c) ? (
                        <rect key={`${r}-${c}`} x={c * cellSize + 0.1} y={r * cellSize + 0.1}
                            width={cellSize - 0.3} height={cellSize - 0.3} fill="#111"/>
                    ) : null;
                })
            )}
            {finderBlock(0, 0)}
            {finderBlock((cells - 7) * cellSize, 0)}
            {finderBlock(0, (cells - 7) * cellSize)}
        </svg>
    );
};

const StarRating = ({ onClose }) => {
    const [hovered, setHovered] = useState(0);
    const [selected, setSelected] = useState(0);
    const [thanked, setThanked] = useState(false);
    const overlayRef = useRef(null);
    const cardRef = useRef(null);
    const thankRef = useRef(null);

    useEffect(() => {
        gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 });
        gsap.fromTo(cardRef.current, { y: 60, opacity: 0, scale: 0.9 }, { y: 0, opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.4)' });
    }, []);

    const handleStar = (star) => {
        setSelected(star);
        setThanked(true);
        gsap.fromTo('.tv-star-btn', { scale: 1 }, { scale: 1.3, duration: 0.15, stagger: 0.05, yoyo: true, repeat: 1, ease: 'power2.out' });
        setTimeout(() => {
            gsap.to(cardRef.current, { y: 40, opacity: 0, scale: 0.92, duration: 0.4, ease: 'power2.in' });
            gsap.to(overlayRef.current, { opacity: 0, duration: 0.4, delay: 0.2, onComplete: onClose });
        }, 1400);
    };

    return (
        <div className="tv-overlay" ref={overlayRef} role="dialog" aria-modal="true" aria-labelledby="rating-title">
            <div className="tv-rating-card" ref={cardRef}>
                {!thanked ? (
                    <>
                        <div className="tv-rating-asterisk-wrap" aria-hidden="true">
                            <img src={asterisk} alt="" className="tv-rating-asterisk" />
                        </div>
                        <h3 className="tv-rating-title" id="rating-title">Rate This Event</h3>
                        <p className="tv-rating-sub">How was your experience?</p>
                        <div className="tv-stars-row" role="group" aria-label="Rate from 1 to 5 stars">
                            {[1, 2, 3, 4, 5].map(s => (
                                <button
                                    key={s}
                                    className={`tv-star-btn${hovered >= s || selected >= s ? ' tv-star-btn--lit' : ''}`}
                                    onMouseEnter={() => setHovered(s)}
                                    onMouseLeave={() => setHovered(0)}
                                    onClick={() => handleStar(s)}
                                    aria-label={`Rate ${s} star${s > 1 ? 's' : ''}`}
                                >
                                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                    </svg>
                                </button>
                            ))}
                        </div>
                        <button className="tv-rating-cancel" onClick={onClose} aria-label="Close rating dialog">Cancel</button>
                    </>
                ) : (
                    <div className="tv-thank-wrap" ref={thankRef} role="status" aria-live="polite">
                        <div className="tv-thank-stars" aria-hidden="true">
                            {[1,2,3,4,5].map(s => (
                                <svg key={s} viewBox="0 0 24 24" fill={s <= selected ? '#FF1E1E' : '#333'} width="22" height="22">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                </svg>
                            ))}
                        </div>
                        <h3 className="tv-thank-title">Thank You!</h3>
                        <p className="tv-thank-sub">Your review has been submitted.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const TicketViewPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showRating, setShowRating] = useState(false);

    const ticket = location.state || { ticketId: 'TDS-001', tierName: 'General Admission', fullName: 'Guest User', totalPaid: 100, currency: 'EGP' };

    const topbarRef = useRef(null);
    const glowRef = useRef(null);
    const cardRef = useRef(null);
    const holderRef = useRef(null);
    const infoRef = useRef(null);
    const btnsRef = useRef(null);
    const asteriskImgRef = useRef(null);

    useEffect(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        gsap.set(topbarRef.current, { opacity: 0, y: -24 });
        gsap.set(glowRef.current, { opacity: 0, scale: 0.6 });
        gsap.set(cardRef.current, { opacity: 0, y: 70, rotationX: 10, scale: 0.93 });
        gsap.set(holderRef.current, { opacity: 0, y: 20 });
        gsap.set(infoRef.current, { opacity: 0, x: -20 });
        gsap.set(btnsRef.current, { opacity: 0, y: 28 });
        gsap.set(asteriskImgRef.current, { opacity: 0, rotation: -90, scale: 0.5 });

        tl.to(topbarRef.current, { opacity: 1, y: 0, duration: 0.6 })
          .to(glowRef.current, { opacity: 1, scale: 1, duration: 1, ease: 'expo.out' }, '-=0.3')
          .to(cardRef.current, { opacity: 1, y: 0, rotationX: 0, scale: 1, duration: 1, ease: 'expo.out' }, '-=0.7')
          .to(asteriskImgRef.current, { opacity: 1, rotation: 0, scale: 1, duration: 0.8, ease: 'elastic.out(1, 0.5)' }, '-=0.5')
          .to(holderRef.current, { opacity: 1, y: 0, duration: 0.5 }, '-=0.3')
          .to(infoRef.current, { opacity: 1, x: 0, duration: 0.5 }, '-=0.3')
          .to(btnsRef.current, { opacity: 1, y: 0, duration: 0.5 }, '-=0.3');

        gsap.to(asteriskImgRef.current, { rotation: 360, duration: 12, repeat: -1, ease: 'none', delay: 2 });

        gsap.to('.tv-scan-line', {
            y: 140,
            duration: 1.8,
            repeat: -1,
            ease: 'none',
            yoyo: true,
            delay: 1.5,
        });
    }, []);

    return (
        <div className="home-wrapper">
            <div className="mobile-container">
                <div className="tv-screen">

                    <header className="tv-topbar" ref={topbarRef}>
                        <button className="tv-icon-btn" onClick={() => navigate(-1)} aria-label="Go back">
                            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 5l-7 7 7 7"/>
                            </svg>
                        </button>
                        <div className="tv-topbar-center">
                            <h1 className="tv-topbar-title">YOUR TICKET</h1>
                        </div>
                        <button className="tv-icon-btn" aria-label="Share ticket">
                            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                            </svg>
                        </button>
                    </header>

                    <main className="tv-body">

                        <div className="tv-glow" aria-hidden="true" ref={glowRef}></div>

                        <article className="tv-card" ref={cardRef} aria-labelledby="ticket-event-name">

                            <div className="tv-card-header">
                                <div className="tv-logo-block" aria-hidden="true">
                                    <div className="tv-logo-sq-outer"></div>
                                    <div className="tv-logo-sq-inner"></div>
                                </div>
                                <div className="tv-header-text">
                                    <p className="tv-brand">THE DESIGN SHOW</p>
                                    <p className="tv-brand-year">CAIRO 2026</p>
                                </div>
                                <span className="tv-exhibition-tag">EXHIBITION</span>
                            </div>

                            <div className="tv-card-divider" aria-hidden="true"></div>

                            <div className="tv-card-event">
                                <h2 className="tv-event-name" id="ticket-event-name">Design & Code</h2>
                                <span className="tv-tier-badge" aria-label={`Ticket type: ${ticket.tierName}`}>{ticket.tierName}</span>
                            </div>

                            <div className="tv-card-meta-grid" role="grid" aria-label="Event Details">
                                <div className="tv-card-meta-item" role="row">
                                    <span className="tv-card-meta-icon" aria-hidden="true">
                                        <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2"/>
                                            <line x1="16" y1="2" x2="16" y2="6"/>
                                            <line x1="8" y1="2" x2="8" y2="6"/>
                                            <line x1="3" y1="10" x2="21" y2="10"/>
                                        </svg>
                                    </span>
                                    <p className="tv-card-meta-label">DATE</p>
                                    <p className="tv-card-meta-val">May 20, 2026</p>
                                </div>
                                <div className="tv-card-meta-item" role="row">
                                    <span className="tv-card-meta-icon" aria-hidden="true">
                                        <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"/>
                                            <polyline points="12 6 12 12 16 14"/>
                                        </svg>
                                    </span>
                                    <p className="tv-card-meta-label">TIME</p>
                                    <p className="tv-card-meta-val">2:00 PM</p>
                                </div>
                                <div className="tv-card-meta-item tv-card-meta-item--full" role="row">
                                    <span className="tv-card-meta-icon" aria-hidden="true">
                                        <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                            <circle cx="12" cy="10" r="3"/>
                                        </svg>
                                    </span>
                                    <p className="tv-card-meta-label">LOCATION</p>
                                    <p className="tv-card-meta-val">Cairo Exhibition Center</p>
                                    <p className="tv-card-meta-sub">Nasr City, Cairo, Egypt</p>
                                </div>
                            </div>

                            <div className="tv-perf-strip" aria-hidden="true">
                                <div className="tv-perf-hole tv-perf-hole--l"></div>
                                <div className="tv-perf-dashes"></div>
                                <div className="tv-perf-hole tv-perf-hole--r"></div>
                            </div>

                            <section className="tv-qr-section" aria-label="Ticket QR Code for scanning">
                                <div className="tv-qr-frame" aria-hidden="true">
                                    <div className="tv-qr-corner tv-qr-corner--tl"></div>
                                    <div className="tv-qr-corner tv-qr-corner--tr"></div>
                                    <div className="tv-qr-corner tv-qr-corner--bl"></div>
                                    <div className="tv-qr-corner tv-qr-corner--br"></div>
                                    <div className="tv-qr-inner">
                                        <QRCode ticketId={ticket.ticketId} />
                                    </div>
                                    <div className="tv-scan-line"></div>
                                </div>
                                <p className="tv-ticket-id-label">TICKET ID</p>
                                <p className="tv-ticket-id">{ticket.ticketId}</p>
                            </section>

                        </article>

                        <section className="tv-holder-strip" ref={holderRef} aria-label="Ticket Holder Information">
                            <div className="tv-holder-left">
                                <p className="tv-holder-label">TICKET HOLDER</p>
                                <p className="tv-holder-name">{ticket.fullName}</p>
                            </div>
                            <div className="tv-holder-divider" aria-hidden="true"></div>
                            <div className="tv-holder-right">
                                <p className="tv-holder-label">PRICE PAID</p>
                                <p className="tv-holder-price">
                                    <span className="sr-only">Total: </span>
                                    {(ticket.totalPaid || 100).toLocaleString()}
                                    <span className="tv-holder-currency"><span className="sr-only"> </span>{ticket.currency}</span>
                                </p>
                            </div>
                        </section>

                        <aside className="tv-info-banner" ref={infoRef} aria-labelledby="info-banner-title">
                            <div className="tv-info-icon-wrap" aria-hidden="true">
                                <img src={asterisk} alt="" className="tv-info-star" ref={asteriskImgRef} />
                            </div>
                            <div className="tv-info-text-block">
                                <p className="tv-info-title" id="info-banner-title">Important Information</p>
                                <p className="tv-info-body">Present this QR code at the entrance. Screenshots are accepted. Please arrive 15 minutes early for check-in and security procedures.</p>
                            </div>
                        </aside>

                    </main>

                    <footer className="tv-footer" ref={btnsRef}>
                        <button className="tv-footer-btn tv-footer-btn--ghost" aria-label="Download ticket as PDF">
                            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg> Download
                        </button>
                        <button className="tv-footer-btn2 tv-footer-btn--ghost2" onClick={() => setShowRating(true)} aria-label="Rate this event">
                            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                            </svg>
                            Rate Event
                        </button>
                    </footer>

                    {showRating && <StarRating onClose={() => setShowRating(false)} />}

                </div>
            </div>
        </div>
    );
};

export default TicketViewPage;